import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BitmapText, Container, useApp } from '@pixi/react';
import { observer } from 'mobx-react-lite';
import gsap from 'gsap';

import { soundService, SoundTypes } from '../../../../../services/soundService';
import { JackpotSymbols, JackpotSymbolsToName } from '../../../../../enums';
import { isMobile, roundNumber } from '../../../../../helpers';
import { isoStandard } from '../../../../helpers';
import { useStore } from '../../../../../hooks';
import { Spine } from '../../../../components';

let timeLine: any;

const JackPotWinPopup = observer(({ winIcon: { winAmount, jackpotCard }, jackpotIcons }: any) => {
  const { stage } = useApp();
  const bitmapRef: any = useRef();
  const jackPotWinPopupRef: any = useRef(null);
  const [popupWinAmount, setPopupWinAmount] = useState<any>('0');
  const [isNumberAnimationEnd, setIsNumberAnimationEnd] = useState(false);
  const { jackpotStore } = useStore()
  const { isShowJackPotWinPopup } = jackpotStore;
  
  const hideJackPotWinPopup = useCallback(() => {
    if (!isShowJackPotWinPopup || !jackPotWinPopupRef.current) return;
    soundService.stop(SoundTypes.JackpotWinPopUp);
    jackPotWinPopupRef.current.children[0].play('end');
    jackPotWinPopupRef.current.children[0].addListener(
      'whiteStart',
      () => {
        jackpotStore.setWinPopupData({ name: JackpotSymbolsToName[jackpotCard as JackpotSymbols], winAmount });
        jackpotStore.reSetJackpotData();
        jackpotStore.resetOpenedJackpotCardCount();
        soundService.stopAll();
        setPopupWinAmount('0');
      },
      null,
      'end'
    );
  }, [isShowJackPotWinPopup, jackpotCard, winAmount, jackpotIcons]);

  const handleCollectNumbersAndFinishJackPot = useCallback(() => {
    if (!isNumberAnimationEnd && isShowJackPotWinPopup) {
      timeLine.kill();
      setPopupWinAmount(isoStandard(winAmount));
      setIsNumberAnimationEnd(true);
    }
  }, [isNumberAnimationEnd, isShowJackPotWinPopup, winAmount]);

  useEffect(() => {
    const jackPotWinPopup = new Spine({ key: 'jackpotWinPopup' });
    jackPotWinPopup.addChildToSlot('amount', bitmapRef.current);
    jackPotWinPopupRef.current.addChild(jackPotWinPopup);
  }, []);

  useEffect(() => {
    const popup = jackPotWinPopupRef.current.children[0];
    soundService.stop(SoundTypes.JackpotBackgroundMusic);
    soundService.play(SoundTypes.JackpotWinPopUp);
    popup.play('start');
    popup.skeleton.setSkinByName(jackpotIcons[jackpotCard]);
    popup.addListener(
      'complete',
      () => {
        popup.play('loop', true);
        setTimeout(() => {
          setIsNumberAnimationEnd(false);
          hideJackPotWinPopup();
        }, 5000);
      },
      null,
      'start'
    );
  }, [jackpotIcons, jackpotCard, hideJackPotWinPopup]);

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        handleCollectNumbersAndFinishJackPot();
      }
    },
    [handleCollectNumbersAndFinishJackPot]
  );

  useEffect(() => {
    timeLine = gsap.timeline();
    bitmapRef.current.anchor.set(0.5);
    timeLine.to(bitmapRef.current, {
      textContent: winAmount,
      duration: 10,
      snap: { textContent: 0.1 },
      stagger: {
        each: 0.1,
      },
      onUpdate: function () {
        setPopupWinAmount(
          isoStandard(
            +roundNumber(
              +this.targets()[0].textContent,
              2
            )
          )
        );
      },
      onComplete: function () {
        setIsNumberAnimationEnd(true);
      },
    });
  }, [bitmapRef, winAmount]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    stage.interactive = true;
    stage.addEventListener('pointerdown', handleCollectNumbersAndFinishJackPot);
    return () => {
      stage.removeEventListener('pointerdown', handleCollectNumbersAndFinishJackPot);
    };
  }, [handleCollectNumbersAndFinishJackPot, stage]);

  return (
    <Container
      x={isMobile() ? 270 : 720}
      y={isMobile() ? 480 : 405}
      cursor='pointer'
      ref={jackPotWinPopupRef}
      name='JackPotWinPopupContainer'
      visible={isShowJackPotWinPopup}
    >
      <BitmapText
        pivot={0.5}
        align='center'
        ref={bitmapRef}
        text={popupWinAmount}
        style={{ fontSize: isMobile() ? 50 : 80, fontName: 'numbersJackpot' }}
      />
    </Container>
  );
});

export default JackPotWinPopup;
